/**
 * Dads Locker — cart drawer extras.
 *
 *  1. Confetti + "bar comes across" when something is ADDED to the cart.
 *  2. "Add a little extra" panel: + Add buttons, variant-picker thumbnail
 *     swap, and a fixed-slot rotation for the first four cards:
 *       - Nitrous Tumbler leads 50% of the time; when it doesn't lead, it
 *         always sits in slot 2.
 *       - Daddy Needs a Beer Cap always sits in slot 3 when it isn't leading.
 *       - Wanderer Mug always sits in slot 4 when it isn't leading.
 *       - Gearstick Hoodie has no fixed home — it leads the remaining ~50%
 *         of sessions (split evenly with Daddy Cap / Wanderer as tie-breaker
 *         leaders), and otherwise slots into whichever position the actual
 *         leader vacated.
 *     Everything after those four is shuffled randomly. Adds made from the
 *     panel carry hidden line-item properties (_dl_addon, _dl_addon_pos,
 *     _dl_addon_leader) so orders show what sold and who was leading.
 */
import { StandardEvents } from '@shopify/events';

const SESSION_KEY = 'dl_cx_plan_v3';

const NITROUS = '10947045032247'; // Nitrous Bottle Insulated Tumbler
const GEARSTICK = '10944979730743'; // The Gearstick Hoodie
const DADDYCAP = '10940235153719'; // The Daddy Needs a Beer Cap
const WANDERER = '10940234989879'; // The Wanderer Enamel Campfire Mug

// Fixed home slot (1-indexed) for these three when they AREN'T leading.
const HOME_SLOT = { [NITROUS]: 2, [DADDYCAP]: 3, [WANDERER]: 4 };

// Nitrous leads half the time; the other half is split evenly between
// Gearstick, Daddy Cap and Wanderer.
const LEADER_WEIGHTS = {
  [NITROUS]: 0.5,
  [GEARSTICK]: 0.5 / 3,
  [DADDYCAP]: 0.5 / 3,
  [WANDERER]: 0.5 / 3,
};

let skipConfettiUntil = 0;
let lastCelebrate = 0;

/* ---------- add-on ordering ---------- */

function pickLeader() {
  const ids = Object.keys(LEADER_WEIGHTS);
  const total = ids.reduce((sum, id) => sum + LEADER_WEIGHTS[id], 0);
  let r = Math.random() * total;
  for (const id of ids) {
    r -= LEADER_WEIGHTS[id];
    if (r <= 0) return id;
  }
  return ids[ids.length - 1];
}

/**
 * Rank (0-indexed) for one of the four rotation candidates, or null if the
 * card isn't one of them (falls back to the random tail order).
 */
function fixedRank(id, leader) {
  if (id === leader) return 0;
  if (id === GEARSTICK) return HOME_SLOT[leader] - 1; // fills whichever slot the leader vacated
  if (HOME_SLOT[id] != null) return HOME_SLOT[id] - 1; // Nitrous / Daddy Cap / Wanderer keep their home slot
  return null;
}

function loadPlan() {
  let plan = null;
  try { plan = JSON.parse(sessionStorage.getItem(SESSION_KEY) || 'null'); } catch (e) { plan = null; }
  if (!plan || !plan.leader) {
    plan = { leader: pickLeader(), ranks: {}, fresh: true };
  }
  return plan;
}

function savePlan(plan) {
  try {
    delete plan.fresh;
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(plan));
  } catch (e) {}
}

function applyOrder() {
  const tracks = document.querySelectorAll('[data-dl-cx-track]');
  if (!tracks.length) return;
  const plan = loadPlan();
  let dirty = !!plan.fresh;

  tracks.forEach((track) => {
    const cards = Array.from(track.querySelectorAll('[data-dl-cx-card]'));
    const scored = cards.map((card) => {
      const id = card.dataset.pid;
      const fixed = fixedRank(id, plan.leader);
      let rank;
      if (fixed != null) {
        rank = fixed;
      } else {
        if (plan.ranks[id] == null) { plan.ranks[id] = 100 + Math.random() * 1000; dirty = true; }
        rank = plan.ranks[id];
      }
      return { card, rank };
    }).sort((a, b) => a.rank - b.rank);

    scored.forEach((s, i) => {
      if (s.card.style.order !== String(i)) s.card.style.order = String(i);
    });
  });

  if (dirty) savePlan(plan);
}

let orderTimer = 0;
function scheduleOrder() {
  clearTimeout(orderTimer);
  orderTimer = setTimeout(applyOrder, 20);
}

new MutationObserver(scheduleOrder).observe(document.body, {
  childList: true,
  subtree: true,
  attributes: true,
  attributeFilter: ['style'],
});
scheduleOrder();

/* ---------- variant dropdown → thumbnail swap ---------- */

document.addEventListener('change', (e) => {
  const select = e.target instanceof Element ? e.target.closest('.dl-cx__select') : null;
  if (!select) return;
  const card = select.closest('[data-dl-cx-card]');
  if (!card) return;

  const btn = card.querySelector('.dl-cx__add');
  if (btn) btn.dataset.variantId = select.value;

  const raw = card.dataset.variantMedia;
  if (!raw) return;
  try {
    const map = JSON.parse(raw);
    const url = map[select.value];
    const img = card.querySelector('.dl-cx__media img');
    if (url && img && img.src !== url) img.src = url;
  } catch (err) {}
});

/* ---------- + Add buttons ---------- */

document.addEventListener('click', async (e) => {
  const btn = e.target instanceof Element ? e.target.closest('.dl-cx__add') : null;
  if (!btn || btn.disabled) return;
  e.preventDefault();

  const card = btn.closest('[data-dl-cx-card]');
  const select = card ? card.querySelector('.dl-cx__select') : null;
  const id = Number(select ? select.value : btn.dataset.variantId);
  if (!id) return;

  const original = btn.textContent;
  btn.disabled = true;
  btn.textContent = 'Adding…';

  const plan = loadPlan();
  const pos = card && card.style.order !== '' ? Number(card.style.order) + 1 : 0;
  const routes = (window.Theme && window.Theme.routes) || {};
  const addUrl = (routes.cart_add_url || '/cart/add') + '.js';
  const cartUrl = (routes.cart_url || '/cart') + '.js';

  try {
    const res = await fetch(addUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{
          id,
          quantity: 1,
          properties: { _dl_addon: 'cart-panel', _dl_addon_pos: String(pos), _dl_addon_leader: plan.leader },
        }],
      }),
    });
    if (!res.ok) throw new Error('add failed');
    const cart = await fetch(cartUrl, { headers: { Accept: 'application/json' } }).then((r) => r.json());

    skipConfettiUntil = Date.now() + 2500; // no second confetti burst for in-drawer adds
    const evt = new Event(StandardEvents.cartLinesUpdate, { bubbles: true });
    evt.action = 'add';
    evt.promise = Promise.resolve({ cart, detail: { itemCount: cart.item_count, didError: false } });
    document.dispatchEvent(evt);
    btn.textContent = 'Added ✓';
  } catch (err) {
    btn.textContent = 'Try again';
    btn.disabled = false;
    setTimeout(() => { btn.textContent = original; }, 1800);
  }
});

/* ---------- confetti when something is added ---------- */

function makeCanvas() {
  const cv = document.createElement('canvas');
  cv.setAttribute('aria-hidden', 'true');
  cv.style.cssText =
    'position:fixed;inset:0;width:100vw;height:100vh;max-width:none;max-height:none;margin:0;padding:0;border:0;' +
    'background:transparent;overflow:visible;pointer-events:none;';
  let inTopLayer = false;
  try {
    if (typeof cv.showPopover === 'function') {
      cv.setAttribute('popover', 'manual');
      document.body.appendChild(cv);
      cv.showPopover();
      inTopLayer = true;
    }
  } catch (err) {
    inTopLayer = false;
  }
  if (!inTopLayer) {
    (document.querySelector('#cart-drawer dialog[open]') || document.body).appendChild(cv);
    cv.style.zIndex = '2147483647';
  }
  return cv;
}

function celebrate() {
  try {
    if (document.hidden && !window.__dlForceConfetti) return;
    const now = Date.now();
    if (now - lastCelebrate < 1200) return;
    lastCelebrate = now;

    const bar = document.querySelector('#cart-drawer [data-dl-cbar]');
    if (!bar) return;
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const fill = bar.querySelector('.dl-cbar__fill');
    const msg = bar.querySelector('.dl-cbar__msg');

    if (reduce) return;

    if (fill && fill.animate) {
      fill.animate([{ transform: 'scaleX(0)' }, { transform: 'scaleX(1)' }], { duration: 900, easing: 'cubic-bezier(.2,.8,.2,1)' });
    }
    if (msg && msg.animate) {
      msg.animate(
        [{ transform: 'scale(1)' }, { transform: 'scale(1.1)', offset: 0.35 }, { transform: 'scale(0.98)', offset: 0.65 }, { transform: 'scale(1)' }],
        { duration: 550, easing: 'ease-out' }
      );
    }

    const r = (bar.querySelector('.dl-cbar__track') || bar).getBoundingClientRect();
    const ox = r.left + r.width / 2;
    const oy = r.top + r.height / 2;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const cv = makeCanvas();
    cv.width = window.innerWidth * dpr;
    cv.height = window.innerHeight * dpr;
    setTimeout(() => { try { cv.remove(); } catch (err) {} }, 4200);
    const ctx = cv.getContext('2d');
    ctx.scale(dpr, dpr);

    const colors = ['#1B3A2B', '#C9A75F', '#F4F1E8', '#2f7d5b', '#e8c987', '#C0392B'];
    const parts = [];
    for (let i = 0; i < 110; i++) {
      const a = Math.random() * Math.PI * 2;
      const sp = 4 + Math.random() * 8;
      parts.push({
        x: ox, y: oy,
        vx: Math.cos(a) * sp,
        vy: Math.sin(a) * sp - 5,
        g: 0.18 + Math.random() * 0.12,
        s: 5 + Math.random() * 6,
        rot: Math.random() * Math.PI,
        vr: (Math.random() - 0.5) * 0.4,
        c: colors[i % colors.length],
      });
    }
    const start = performance.now();
    const frame = (t) => {
      const dt = t - start;
      ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);
      let alive = 0;
      for (const p of parts) {
        p.vy += p.g;
        p.vx *= 0.99;
        p.x += p.vx;
        p.y += p.vy;
        p.rot += p.vr;
        const alpha = 1 - dt / 1900;
        if (alpha <= 0 || p.y > window.innerHeight + 40) continue;
        alive++;
        ctx.save();
        ctx.globalAlpha = alpha;
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.fillStyle = p.c;
        ctx.fillRect(-p.s / 2, -p.s / 2, p.s, p.s * 0.62);
        ctx.restore();
      }
      if (alive > 0 && dt < 2100) requestAnimationFrame(frame);
      else cv.remove();
    };
    requestAnimationFrame(frame);
  } catch (err) {}
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (event.action !== 'add') return;
  if (Date.now() < skipConfettiUntil) return;
  const go = () => setTimeout(celebrate, 600);
  if (event.promise && typeof event.promise.then === 'function') {
    event.promise.then(
      (res) => { if (!(res && res.detail && res.detail.didError)) go(); },
      () => {}
    );
  } else {
    go();
  }
});

// handy for testing in devtools: window.__dlCelebrate()
window.__dlCelebrate = celebrate;
